/**
 * @file resolveSSOLinks.js
 * @description Builds, recognizes and resolves rich-text SSO links. Links are stored either as the
 * in-app marker (https://ssoId:{id}) or, when they must work outside the app (e.g. notification
 * emails/SMS), as an absolute app URL (/sso-link/{id}) that resolves the login when clicked.
 */

import {
    fetchClientSSOById,
    fetchSystemSSOById,
    loginClientSSO,
} from '../services/ssoService';
import { ROUTE_BUILDERS } from '../constants/routes';
import { buildExternalAppUrl } from './externalAppLinks';
import { hasText, isValidUrl } from './utils';

export const SSO_LOGIN_FAILED_HREF = '#sso-login-failed';

const SSO_MARKER_PREFIX = 'https://ssoId:';
const REGEX_SPECIAL_CHARACTERS = /[.*+?^${}()|[\]\\/]/g;

function escapeRegExp(value) {
    return value.replace(REGEX_SPECIAL_CHARACTERS, '\\$&');
}

function buildSSOLinkSource() {
    const appSSOLinkPrefix = `${window.location.origin}${ROUTE_BUILDERS.ssoLink('')}`;

    return `(?:${escapeRegExp(SSO_MARKER_PREFIX)}|${escapeRegExp(appSSOLinkPrefix)})(\\d+)(?![\\d/])(?:\\?[^\\s"'<>#]*)?`;
}

function isHttpUrl(url) {
    if (!isValidUrl(url)) {
        return false;
    }

    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
}

export function buildSSOMarkerUrl(tenantSSOId) {
    return `${SSO_MARKER_PREFIX}${tenantSSOId}`;
}

export function buildAbsoluteSSOLinkUrl(tenantSSOId) {
    return buildExternalAppUrl(ROUTE_BUILDERS.ssoLink(tenantSSOId));
}

export function getSSOLinkId(url) {
    if (typeof url !== 'string' || !url) {
        return null;
    }

    const match = url.match(new RegExp(`^${buildSSOLinkSource()}$`));
    return match ? match[1] : null;
}

export async function requestSSOLoginUrl(tenantSSOId) {
    let ssoData = await fetchClientSSOById(tenantSSOId);

    if (!hasText(ssoData?.url)) {
        ssoData = await fetchSystemSSOById(ssoData.ssoId);
    }

    const loginResponse = await loginClientSSO(ssoData);
    const loginUrl = loginResponse?.login?.saml20?.url;

    return isHttpUrl(loginUrl) ? loginUrl : null;
}

export async function resolveSSOLinks(text) {
    if (!text) {
        return text;
    }

    const ssoLinkPattern = new RegExp(buildSSOLinkSource(), 'g');
    const ssoIds = new Set(
        Array.from(text.matchAll(ssoLinkPattern), match => match[1]),
    );

    if (ssoIds.size === 0) {
        return text;
    }

    const ssoCache = new Map();
    const fetchPromises = Array.from(ssoIds).map(async ssoId => {
        try {
            const loginUrl = await requestSSOLoginUrl(ssoId);
            ssoCache.set(ssoId, loginUrl || SSO_LOGIN_FAILED_HREF);

            ssoCache.set(
                ssoId,
                isValidUrl(loginUrl) ? loginUrl : SSO_LOGIN_FAILED_HREF,
            );
        } catch (error) {
            console.error('Error resolving SSO link:', error);
            ssoCache.set(ssoId, SSO_LOGIN_FAILED_HREF);
        }
    });

    await Promise.all(fetchPromises);

    return text.replace(
        ssoLinkPattern,
        (match, ssoId) => ssoCache.get(ssoId) ?? match,
    );
}
